import { useNavigate } from "react-router-dom";
import { RotateCcw } from "lucide-react";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";
import OllieAvatar from "components/ollie-avatar/ollie-avatar";
import routeNames from "constants/route-names";

interface PlanReviewLoadErrorProps {
	/** What could not be opened, when it is not the plan review itself. */
	subject?: string;
	/** The read went through and the row is not there — a stale link, not a failure. */
	notFound?: boolean;
}

/**
 * What a plan review screen shows when it has no data to draw.
 *
 * These screens run on subscriptions, so a socket that dies — an expired session, a
 * laptop coming back from sleep — leaves them empty, and an empty screen reads as a
 * project with nothing in it. Reloading the page is what actually fixes that, so that
 * is the button: sending the user back to the dashboard only makes them open the same
 * card again. A row that genuinely is not there is the other case, and reloading would
 * not help, so it says so and points at the dashboard instead.
 */
const PlanReviewLoadError = ({ subject = "this plan review", notFound = false }: PlanReviewLoadErrorProps) => {
	const navigate = useNavigate();

	return (
		<OllieLayout>
			<div className="psf-page">
				<div className="prle-panel">
					<OllieAvatar className="prle-avatar" />

					{notFound ? (
						<>
							<p className="prle-title">We couldn&apos;t find {subject}.</p>
							<p className="prle-text">
								It may have been removed, or the link may point somewhere you can&apos;t open.
							</p>
							<button
								type="button"
								className="prle-btn"
								onClick={() => navigate(routeNames.OLLIE_DASHBOARD)}
							>
								Back to dashboard
							</button>
						</>
					) : (
						<>
							<p className="prle-title">Oops! We&apos;re having trouble opening {subject}.</p>
							<p className="prle-text">Your session may have gone stale. Reloading the page usually sorts it out.</p>
							<button type="button" className="prle-btn" onClick={() => window.location.reload()}>
								<RotateCcw size={16} />
								Try again
							</button>
						</>
					)}
				</div>
			</div>
		</OllieLayout>
	);
};

export default PlanReviewLoadError;
